// Pure helpers ported from ws2k13/assets/js/ssl/utilities/shared.js + the fetch/post
// data handlers. Kept side-effect-free so they can be unit-tested directly.

import type {
  AssignPayload,
  OrgFormData,
  OvEvValidations,
  ProductType,
  RpcResponse,
  SSLStatus,
  ValidationStatus,
} from './types';

/** Default RPC base — the Catalyst chain root for all SSL calls. */
export const DEFAULT_BASE_URL = '/cp/rpc/rtr/ssl';

/** Product display name — mirrors getCertificateName. `null`/unknown product falls to EV. */
export function getCertificateName(productType: ProductType | null, isWildcard: boolean): string {
  if (productType === 'Simple') return isWildcard ? 'Simple Wildcard' : 'Simple';
  if (productType === 'Organisational')
    return isWildcard ? 'Organisational Wildcard' : 'Organisational';
  return 'Extended Validation';
}

/** True for the OV/EV products that require the Organisation Details section. */
export function requiresOrgDetails(productType: ProductType | null): boolean {
  return productType === 'Organisational' || productType === 'Extended';
}

const STATUS_LABELS: Record<SSLStatus, string> = {
  NEW: 'New',
  PENDING: 'Pending',
  ACTIVE: 'Active',
  EXPIRED: 'Expired',
  CANCELLED: 'Cancelled',
  RENEWED: 'Renewed',
};

/** Title-case status label — mirrors getStatusName. */
export function getStatusName(status: SSLStatus): string {
  return STATUS_LABELS[status];
}

/** Badge variant for a status pill (hi-ui). Legacy used bespoke colour classes. */
export function getStatusBadgeVariant(
  status: SSLStatus
): 'info' | 'warning' | 'success' | 'neutral' | 'error' {
  switch (status) {
    case 'NEW':
      return 'info';
    case 'PENDING':
      return 'warning';
    case 'ACTIVE':
      return 'success';
    case 'EXPIRED':
    case 'CANCELLED':
      return 'error';
    default:
      return 'neutral';
  }
}

// --- Validation regexes (verbatim from section-form.js / shared.js) ---
export const CSR_REGEX =
  /^-----BEGIN CERTIFICATE REQUEST-----[\s\S]+-----END CERTIFICATE REQUEST-----$/;
export const PHONE_REGEX = /^\+[0-9]{1,3}\.[0-9]{1,14}$/;
/* eslint-disable no-control-regex -- ASCII/email ranges ported verbatim from the legacy regexes */
export const ASCII_REGEX = /^[\x00-\x7F\s]*$/;

export const EMAIL_REGEX =
  /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
/* eslint-enable no-control-regex */

/** Whole-CSR shape check — mirrors isValidCsr. */
export function isValidCsr(csr: string): boolean {
  return CSR_REGEX.test(csr.trim());
}

/**
 * Subdomain validation — mirrors isValidSubdomain. Returns an error message, or `null`
 * when valid. An empty value is "not valid" but shows no message (matches legacy).
 */
export function validateSubdomain(value: string): { valid: boolean; error: string | null } {
  if (value === '') return { valid: false, error: null };
  if (!/^[a-z0-9.-]+$/.test(value))
    return { valid: false, error: 'can only contain letters, numbers, full stops and hyphens' };
  if (/^[^a-z0-9]|[^a-z0-9]$/.test(value))
    return { valid: false, error: 'can only begin and end with a letter or number' };
  if (value.includes('..')) return { valid: false, error: 'cannot contain consecutive full stops' };
  if (value === 'www')
    return { valid: false, error: 'Not needed as the www subdomain is included for free' };
  return { valid: true, error: null };
}

/** Org-field validation — mirrors validateInput (empty/required + regex). Returns message or null. */
export function validateOrgField(
  value: string,
  required: boolean,
  regex: RegExp,
  isPhone = false
): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return required ? 'This field is required' : null;
  if (!regex.test(value))
    return isPhone ? 'Intl. format required, e.g. +44.1234567891' : 'ASCII chars only.';
  return null;
}

/**
 * Common name for the assign payload — mirrors the legacy resolution:
 * CSR path → the decoded common name; else subdomain.domain; else www.domain.
 */
export function buildCommonName(csr: string | null, subdomain: string, domainName: string): string {
  if (csr) return domainName;
  if (subdomain) return `${subdomain}.${domainName}`;
  return `www.${domainName}`;
}

/** Null-if-empty coercion — legacy sends null for untouched fields. */
function nn(v: string | null): string | null {
  return v && v.trim().length > 0 ? v : null;
}

/** Assemble the assign payload — mirrors assignSSL(). */
export function buildAssignPayload(args: {
  commonName: string;
  csr: string | null;
  installTo: string | null;
  form: OrgFormData;
}): AssignPayload {
  const { commonName, csr, installTo, form } = args;
  const parts = [form.address1, form.address2].filter((p) => p && p.trim().length > 0);
  const address = parts.length > 0 ? parts.join(', ') : null;
  return {
    common_name: commonName,
    csr: nn(csr),
    install_to: installTo,
    organisation: nn(form.organisation),
    address,
    city: nn(form.city),
    state: nn(form.state),
    postcode: nn(form.postcode),
    country: nn(form.country),
    approver: {
      first_name: nn(form.firstName),
      last_name: nn(form.lastName),
      email: nn(form.email),
      phone: nn(form.phone),
      job_title: nn(form.jobTitle),
    },
  };
}

/** Registrable domain of a common name — mirrors removeSubFromDomain (handles .co.uk). */
export function removeSubFromDomain(commonName: string): string {
  const match = commonName.match(/([a-z0-9-*]+\.(?:[a-z]{2,}|(?:[a-z]{2,3})\.[a-z]{2,}))$/);
  return match ? match[1] : commonName;
}

/** The DCV verification email usernames offered for the EMAIL method. */
export const DCV_EMAIL_USERNAMES = [
  'admin',
  'administrator',
  'hostmaster',
  'postmaster',
  'webmaster',
] as const;

/** OV/EV validation section labels — mirrors getSectionName. */
export const OV_EV_SECTION_LABELS: Record<keyof OvEvValidations, string> = {
  agreement: 'Subscriber Agreement',
  docs: 'Documentation',
  organization: 'Organisation Check',
  voice: 'Voice Verification',
  whois: 'Whois Records',
};

/** Per-item status text — mirrors getSectionText. */
export function getValidationStatusText(status: ValidationStatus): string {
  switch (status) {
    case 'VALIDATED':
      return 'This has been validated';
    case 'ATTENTION':
      return 'This has an issue and we will be in touch';
    case 'WAITING':
      return 'We are currently reviewing this';
    default:
      return '';
  }
}

/** hi-ui icon for a validation status — validated shows a check, in-review a clock
    (pending), everything else a warning (legacy tick/clock/warning). */
export function getValidationStatusIcon(
  status: ValidationStatus
): 'check-circle-green' | 'clock' | 'error-circle' {
  switch (status) {
    case 'VALIDATED':
      return 'check-circle-green';
    case 'WAITING':
      return 'clock';
    default:
      return 'error-circle';
  }
}

/** Validation-row status-accent modifier — colours match the hi-ui status Badges. */
export function getValidationStepModifier(status: ValidationStatus): string {
  switch (status) {
    case 'VALIDATED':
      return 'validated';
    case 'ATTENTION':
      return 'attention';
    default:
      return 'waiting';
  }
}

// --- URL builders (canonical single-slash routes; backend also accepts the legacy double slash) ---
export const endpoints = {
  info: (base: string, oid: string) => `${base}/${oid}/info`,
  check: (base: string, oid: string) => `${base}/${oid}/check`,
  assign: (base: string, oid: string) => `${base}/${oid}/assign`,
  reissue: (base: string, oid: string) => `${base}/${oid}/reissue`,
  resend: (base: string, oid: string) => `${base}/${oid}/resend`,
  revoke: (base: string, oid: string) => `${base}/${oid}/revoke`,
  download: (base: string, oid: string) => `${base}/${oid}/download`,
  decodecsr: (base: string) => `${base}/decodecsr`,
};

const SERVER_ERROR = 'There was a server error. Please contact support.';
const LOGIN_EXPIRED = 'Login expired. Please refresh the page.';

/** Map an HTTP response to the {data,error} envelope — mirrors fetch-handler / post-handler. */
async function toEnvelope<T>(res: Response): Promise<RpcResponse<T>> {
  let body: RpcResponse<T> | null;
  try {
    body = (await res.json()) as RpcResponse<T>;
  } catch {
    return { data: null, error: 'Invalid JSON response from server.' };
  }
  if (res.status >= 500) return { data: null, error: SERVER_ERROR };
  if (res.status === 302) return { data: null, error: LOGIN_EXPIRED };
  if (!res.ok) return { data: null, error: body?.error ?? SERVER_ERROR };
  return { data: body?.data ?? null, error: body?.error ?? null };
}

/**
 * A deliberate abort (component unmount / superseded request) is re-thrown so
 * signal-aware callers can ignore it; any other fetch rejection (network failure)
 * is converted to the generic {error} envelope so mutation handlers don't hang on
 * a rejected promise with the spinner stuck.
 */
function rejectionToEnvelope<T>(err: unknown): RpcResponse<T> {
  // A fetch abort throws a DOMException (not an Error subclass in every runtime),
  // so match on the name rather than the prototype.
  if ((err as { name?: string })?.name === 'AbortError') throw err;
  return { data: null, error: SERVER_ERROR };
}

/**
 * GET an RPC endpoint. Never rejects on a network error — it surfaces as {error};
 * a deliberate AbortError is re-thrown for callers that pass a signal.
 */
export async function rpcGet<T>(
  fetchFn: typeof window.fetch,
  url: string,
  signal?: AbortSignal
): Promise<RpcResponse<T>> {
  let res: Response;
  try {
    res = await fetchFn(url, { signal });
  } catch (err) {
    return rejectionToEnvelope<T>(err);
  }
  return toEnvelope<T>(res);
}

/**
 * POST an RPC endpoint with a JSON body — mirrors postData (Content-Type:
 * application/json). Never rejects on a network error — it surfaces as {error};
 * a deliberate AbortError is re-thrown for callers that pass a signal.
 */
export async function rpcPost<T>(
  fetchFn: typeof window.fetch,
  url: string,
  body: unknown,
  signal?: AbortSignal
): Promise<RpcResponse<T>> {
  let res: Response;
  try {
    res = await fetchFn(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    return rejectionToEnvelope<T>(err);
  }
  return toEnvelope<T>(res);
}
